export const dateFormatter = new Intl.DateTimeFormat("ja-JP", {
	year: "numeric",
	month: "2-digit",
	day: "2-digit",
});

export const timeFormatter = new Intl.DateTimeFormat("ja-JP", {
	hour: "2-digit",
	minute: "2-digit",
	hour12: false,
});

export const dateTimeFormatter = new Intl.DateTimeFormat("ja-JP", {
	year: "numeric",
	month: "2-digit",
	day: "2-digit",
	hour: "2-digit",
	minute: "2-digit",
	hour12: false,
});

export function formatTime(iso: string): string {
	if (!iso) return "";
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return "";
	return timeFormatter.format(d);
}

export function formatDateTime(iso: string): string {
	if (!iso) return "";
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return "";
	return dateTimeFormatter.format(d);
}

const relativeTimeFormatter = new Intl.RelativeTimeFormat("ja-JP", { numeric: "auto" });

const RELATIVE_UNITS: { unit: Intl.RelativeTimeFormatUnit; seconds: number }[] = [
	{ unit: "year", seconds: 60 * 60 * 24 * 365 },
	{ unit: "month", seconds: 60 * 60 * 24 * 30 },
	{ unit: "day", seconds: 60 * 60 * 24 },
	{ unit: "hour", seconds: 60 * 60 },
	{ unit: "minute", seconds: 60 },
	{ unit: "second", seconds: 1 },
];

export function formatRelativeTime(iso: string): string {
	if (!iso) return "";
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return "";
	const diffSec = (d.getTime() - Date.now()) / 1000;
	const absSec = Math.abs(diffSec);
	for (const { unit, seconds } of RELATIVE_UNITS) {
		if (absSec >= seconds || unit === "second") {
			return relativeTimeFormatter.format(Math.round(diffSec / seconds), unit);
		}
	}
	return "";
}

/**
 * 経過時間 (ミリ秒) を「45秒」「3分12秒」「1時間5分」形式の日本語で返す。
 * モニターの実行時間表示など、開始からの相対的な長さを示す箇所向け。
 */
export function formatElapsed(ms: number): string {
	const totalSeconds = Math.max(0, Math.floor(ms / 1000));
	const seconds = totalSeconds % 60;
	const minutes = Math.floor(totalSeconds / 60) % 60;
	const hours = Math.floor(totalSeconds / 3600);
	if (hours > 0) return `${hours}時間${minutes}分`;
	if (minutes > 0) return `${minutes}分${seconds}秒`;
	return `${seconds}秒`;
}

/**
 * トークン数をコンパクトに表示する。1,000 未満は整数、以上は k / M 単位で小数第1位まで表示する。
 * ターン累積の入力トークンは容易に 1M を超えるため、k 止まりだと「1500k」になる。
 */
export function formatTokenCount(tokens: number): string {
	if (!Number.isFinite(tokens)) return "-";
	if (tokens < 1_000) return tokens.toLocaleString();
	// 単位より先に丸める。逆順だと 999,950 が "1000k" になり、k 止まりを避けた意味が無くなる。
	const k = Math.round((tokens / 1_000) * 10) / 10;
	if (k < 1_000) return `${k}k`;
	return `${Math.round((tokens / 1_000_000) * 10) / 10}M`;
}

function startOfDay(d: Date): number {
	return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/**
 * 当日は「今日 HH:mm」、前日は「昨日 HH:mm」、それ以前は通常の日時表記で返す。
 * 最終アクセスなど、直近かどうかが一目で分かると便利な箇所向け。
 */
export function formatRelativeDateTime(iso: string): string {
	if (!iso) return "";
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return "";
	const day = dayLabel(d);
	return day ? `${day} ${timeFormatter.format(d)}` : dateTimeFormatter.format(d);
}

/** 当日は「今日」、前日は「昨日」、それ以前は null。 */
function dayLabel(d: Date): string | null {
	const dayDiff = Math.round((startOfDay(new Date()) - startOfDay(d)) / (1000 * 60 * 60 * 24));
	if (dayDiff === 0) return "今日";
	if (dayDiff === 1) return "昨日";
	return null;
}

const RECENT_MS = 60 * 60 * 1000;

/**
 * 1 分未満は「たった今」、1 時間未満は「5 分前」、それより前は「今日」「昨日」や日付だけを返す。
 * 相対表記はチャットのメッセージの時刻 (RelativeTimestamp) と揃える。
 */
export function formatRecentTimeOrDate(iso: string): string {
	if (!iso) return "";
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return "";
	const ageMs = Date.now() - d.getTime();
	if (ageMs < 60_000) return "たった今";
	// formatRelativeTime と同じ丸めだと 59 分半から「60 分前」になるため、59 分で止める。
	if (ageMs < RECENT_MS)
		return relativeTimeFormatter.format(Math.max(-59, Math.round(-ageMs / 60_000)), "minute");
	return dayLabel(d) ?? dateFormatter.format(d);
}
